import { Component } from 'react';
import { ErrorFallbackScreen } from './ErrorFallbackScreen';

interface Props {
  children: React.ReactNode;
  /** Called once when the boundary catches a render-phase error. Use this
   *  to ship the error to the consumer app's error tracker / structured
   *  logging — keeps this component framework-agnostic. */
  onError?: ((error: unknown) => void) | undefined;
}

interface State {
  hasError: boolean;
  message: string;
}

/**
 * App-root boundary — catches uncaught render-phase errors anywhere in the
 * tree and renders the shared `ErrorFallbackScreen` (bare RN primitives, no
 * theme provider, so it survives a provider crash). Per-route isolation is
 * handled separately by `RouteErrorBoundary`.
 */

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, message: '' };

  static getDerivedStateFromError(error: unknown): State {
    const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
    return { hasError: true, message };
  }

  override componentDidCatch(error: unknown): void {
    try {
      this.props.onError?.(error);
    } catch {
      // Never let the error reporter throw out of the boundary itself.
    }
  }

  reset = () => {
    this.setState({ hasError: false, message: '' });
  };

  override render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <ErrorFallbackScreen
        title="Something went wrong"
        message={this.state.message}
        actionLabel="Try again"
        onAction={this.reset}
      />
    );
  }
}
